import React, { useEffect, useState } from "react";
import { Phone, MapPin, Clock, Mail, Star, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchStudio, fetchReviews, submitContact } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

export const Contact = () => {
  const [studio, setStudio] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchStudio().then(setStudio);
    fetchReviews().then(setReviews);
  }, []);

  const send = async () => {
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message");
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      toast.success("Message sent ♡ We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-10 px-6 pb-4" data-testid="contact-page">
      <p className="script text-2xl text-gold">come say hi</p>
      <h1 className="text-4xl font-serif text-espresso mb-2 leading-tight">Visit the studio</h1>
      <p className="text-mocha text-sm mb-6">We'd love to meet you. Walk-ins welcome, bookings preferred.</p>

      {/* Location card */}
      <div className="relative rounded-[2rem] overflow-hidden h-48 mb-4 shadow-soft" data-testid="location-map">
        <img
          src="https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=1200&q=80"
          alt="Studio"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 to-transparent flex items-end p-5">
          <div>
            <p className="script text-2xl text-blush">our little home</p>
            <p className="text-cream font-serif text-xl leading-tight">{studio?.name}</p>
          </div>
        </div>
      </div>

      {/* Info blocks */}
      {studio && (
        <div className="space-y-3">
          <a href={`tel:${studio.phone}`} data-testid="contact-phone" className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-soft hover:shadow-glow transition">
            <div className="w-11 h-11 rounded-full bg-blush/30 flex items-center justify-center">
              <Phone size={18} className="text-espresso" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gold">Call</p>
              <p className="text-espresso">{studio.phone}</p>
            </div>
          </a>

          <a href={`mailto:${studio.email}`} data-testid="contact-email" className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-soft hover:shadow-glow transition">
            <div className="w-11 h-11 rounded-full bg-blush/30 flex items-center justify-center">
              <Mail size={18} className="text-espresso" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gold">Email</p>
              <p className="text-espresso">{studio.email}</p>
            </div>
          </a>

          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(studio.address)}`}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-address"
            className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-soft hover:shadow-glow transition"
          >
            <div className="w-11 h-11 rounded-full bg-blush/30 flex items-center justify-center">
              <MapPin size={18} className="text-espresso" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-gold">Visit</p>
              <p className="text-espresso text-sm">{studio.address}</p>
            </div>
          </a>

          {/* Hours */}
          <div className="bg-white rounded-2xl p-4 shadow-soft" data-testid="contact-hours">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-blush/30 flex items-center justify-center">
                <Clock size={18} className="text-espresso" />
              </div>
              <p className="text-xs uppercase tracking-widest text-gold">Hours</p>
            </div>
            <div className="space-y-1.5 text-sm">
              {Object.entries(studio.hours).map(([day, hrs]) => (
                <div key={day} className="flex justify-between">
                  <span className="text-mocha">{day}</span>
                  <span className={hrs === "Closed" ? "text-blushDeep" : "text-espresso"}>{hrs}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* Reviews */}
      <div className="mt-12">
        <p className="script text-2xl text-gold">kind words</p>
        <h2 className="text-3xl font-serif text-espresso mb-4">Reviews</h2>
        <div className="space-y-3" data-testid="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white rounded-[1.75rem] p-5 shadow-soft" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-1 mb-2">
                {[...Array(r.rating)].map((_, i) => (
                  <Star key={i} size={13} className="fill-gold text-gold" />
                ))}
              </div>
              <p className="text-espresso font-serif italic text-lg leading-snug">"{r.text}"</p>
              <div className="flex items-center justify-between mt-3">
                <p className="text-espresso text-sm font-medium">{r.name}</p>
                <p className="text-mocha text-xs">{r.service}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact form */}
      <div className="mt-12">
        <p className="script text-2xl text-gold">questions?</p>
        <h2 className="text-3xl font-serif text-espresso mb-4">Send us a message</h2>
        <div className="space-y-3 bg-white rounded-[2rem] p-5 shadow-soft" data-testid="contact-form">
          <div>
            <Label className="text-mocha text-xs uppercase tracking-widest">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 rounded-2xl border-borderSoft bg-cream h-12 px-4"
              data-testid="contact-name-input"
            />
          </div>
          <div>
            <Label className="text-mocha text-xs uppercase tracking-widest">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 rounded-2xl border-borderSoft bg-cream h-12 px-4"
              data-testid="contact-email-input"
            />
          </div>
          <div>
            <Label className="text-mocha text-xs uppercase tracking-widest">Phone (optional)</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1 rounded-2xl border-borderSoft bg-cream h-12 px-4"
              data-testid="contact-phone-input"
            />
          </div>
          <div>
            <Label className="text-mocha text-xs uppercase tracking-widest">Message</Label>
            <Textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              className="mt-1 rounded-2xl border-borderSoft bg-cream"
              data-testid="contact-message-input"
            />
          </div>
          <button
            onClick={send}
            disabled={sending}
            data-testid="contact-send-btn"
            className="w-full h-12 rounded-full bg-espresso text-cream text-sm font-medium hover:shadow-glow transition flex items-center justify-center gap-2"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <><Send size={14} /> Send message</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Contact;
